import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Bookmark,
  Copy,
  Check,
  Star,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import { Spot, Language, UserLocation } from '../types/spot';
import { formatDistance, formatTransitBadge } from '../services/geolocation';
import { openKakaoMap } from '../services/mapLinks';
import { useSpotImage } from '../hooks/useSpotImage';
import { getCategoryPlaceholder } from '../services/imageResolver';
import { DayMoment } from '../services/momentEngine';

interface SpotCardProps {
  spot: Spot;
  userLocation: UserLocation;
  language: Language;
  isSaved: boolean;
  isVisited: boolean;
  currentMoment?: DayMoment;
  nearbyClusterCount?: number;
  onToggleSave: (spot: Spot) => void;
  onToggleVisited: (spot: Spot) => void;
  onCopyHangul: (nameKr: string) => void;
  onSelectSpot?: (spot: Spot) => void;
}

export const SpotCard: React.FC<SpotCardProps> = ({
  spot,
  userLocation,
  language,
  isSaved,
  isVisited,
  onToggleSave,
  onToggleVisited,
  onCopyHangul,
  onSelectSpot,
}) => {
  const [copied, setCopied] = useState(false);
  const { imageUrl, isLoading } = useSpotImage(spot);
  const [imgOverride, setImgOverride] = useState<string | null>(null);

  useEffect(() => {
    setImgOverride(null);
  }, [spot.id]);

  const title =
    language === 'de'
      ? spot.nameDe
      : language === 'kr'
      ? spot.nameKr
      : spot.name;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(spot.nameKr);
    setCopied(true);
    onCopyHangul(spot.nameKr);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCardClick = () => {
    if (onSelectSpot) {
      onSelectSpot(spot);
    } else {
      openKakaoMap(spot, false, userLocation);
    }
  };

  const handleImageError = () => {
    setImgOverride(getCategoryPlaceholder(spot.category));
  };

  const currentImage = imgOverride || imageUrl;

  return (
    <article
      className={`card ${isVisited ? 'card--visited' : ''}`}
      onClick={handleCardClick}
      tabIndex={0}
      role="button"
      aria-label={`${title} Details`}
    >
      {/* Hero Image */}
      <div className="card__image-wrap">
        <img
          src={currentImage}
          alt={title}
          onError={handleImageError}
          loading="lazy"
          referrerPolicy="no-referrer"
          className={`card__image ${isLoading ? 'card__image--loading' : ''}`}
        />
        <div className="card__image-overlay" />

        {/* Top Badges & Actions */}
        <div className="card__image-top">
          <div className="card__badge-row">
            {spot.isNew && (
              <span className="badge badge--new">
                ✨ {language === 'kr' ? '신규' : language === 'en' ? 'New' : 'Neu'}
              </span>
            )}

            {spot.isExtra && (
              <span className="badge badge--extra">
                🔥 {language === 'kr' ? '엑스트라' : 'Extra'}
              </span>
            )}

            {spot.calculatedDistance !== undefined && (
              <span
                className="badge badge--distance"
                title={
                  language === 'kr'
                    ? `직선 거리 ${formatDistance(spot.calculatedDistance)}`
                    : language === 'en'
                    ? `Distance ${formatDistance(spot.calculatedDistance)}`
                    : `Entfernung ${formatDistance(spot.calculatedDistance)}`
                }
              >
                {formatTransitBadge(spot.calculatedDistance, language)}
              </span>
            )}

            {spot.isMustSee && !isVisited && (
              <span className="badge badge--must-see">
                {language === 'kr' ? '필수 방문' : 'Must-See'}
              </span>
            )}

            {spot.isEvent && (
              <span className="badge badge--event">
                {language === 'kr' ? '축제·이벤트' : 'Event'}
              </span>
            )}

            {spot.id === 'event-gyeonggi-everland-blood-city' && (
              <span
                className="badge"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #0284c7 100%)',
                  color: '#ffffff',
                  fontWeight: 800,
                  boxShadow: '0 0 10px rgba(16, 185, 129, 0.45)',
                }}
              >
                🗺️ {language === 'kr' ? '5개 존 맵·날씨·맛집' : language === 'en' ? '5-Zone Map & Dining' : 'Park-Map, Wetter & Food'}
              </span>
            )}
          </div>

          <div className="card__action-group">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleVisited(spot);
              }}
              className={`card-icon-btn ${
                isVisited ? 'card-icon-btn--visited' : ''
              }`}
              title={
                isVisited
                  ? language === 'kr'
                    ? '미방문으로 표시'
                    : language === 'en'
                    ? 'Mark as unvisited'
                    : 'Als unbesucht markieren'
                  : language === 'kr'
                  ? '방문 완료'
                  : language === 'en'
                  ? 'Mark as visited'
                  : 'Als besucht abhaken'
              }
              aria-label={isVisited ? 'Als unbesucht markieren' : 'Als besucht abhaken'}
            >
              <CheckCircle2 style={{ width: 16, height: 16 }} />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSave(spot);
              }}
              className={`card-icon-btn ${
                isSaved ? 'card-icon-btn--saved' : ''
              }`}
              title={
                isSaved
                  ? language === 'kr'
                    ? '저장됨'
                    : language === 'en'
                    ? 'Saved'
                    : 'Gemerkt'
                  : language === 'kr'
                  ? '저장하기'
                  : language === 'en'
                  ? 'Save'
                  : 'Merken'
              }
              aria-label={isSaved ? 'Aus Gemerkt entfernen' : 'Zu Gemerkt hinzufügen'}
            >
              <Bookmark
                style={{
                  width: 16,
                  height: 16,
                  fill: isSaved ? 'currentColor' : 'none',
                }}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="card__content">
        {/* Title & Hangul */}
        <div className="card__header">
          <h3 className="card__title" title={title}>{title}</h3>
          <div className="card__hangul-row">
            <span className="card__hangul">{spot.nameKr}</span>
            <button
              type="button"
              onClick={handleCopy}
              className="card__copy-btn"
              title={
                language === 'kr'
                  ? '한글 복사'
                  : language === 'en'
                  ? 'Copy Korean name'
                  : 'Koreanischen Namen kopieren'
              }
              aria-label="Koreanischen Namen kopieren"
            >
              {copied ? (
                <>
                  <Check style={{ width: 12, height: 12, color: 'var(--success)' }} />
                  <span className="text-success">
                    {language === 'kr' ? '복사됨' : language === 'en' ? 'Copied' : 'Kopiert'}
                  </span>
                </>
              ) : (
                <>
                  <Copy style={{ width: 12, height: 12 }} />
                  <span>한글</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Location & Rating */}
        <div className="card__location">
          <MapPin className="card__location-icon" />
          <span>{spot.district || spot.region}</span>
          {spot.region && spot.district && (
            <>
              <span className="card__location-sep">·</span>
              <span>{spot.region}</span>
            </>
          )}
          <div className="card__rating">
            <Star style={{ width: 13, height: 13, fill: '#f59e0b', color: '#f59e0b' }} />
            <span>{spot.rating}</span>
            <span className="card__review-count">
              ({(spot.reviewCount / 1000).toFixed(1)}k)
            </span>
          </div>
        </div>

        {/* Description */}
        <p
          className="card__desc"
          title={language === 'de' ? spot.descriptionDe : spot.description}
        >
          {language === 'de' ? spot.descriptionDe : spot.description}
        </p>

        {/* Subway / Transit info */}
        {spot.subwayInfo && (
          <div className="card__subway">
            <span className="card__subway-icon">🚇</span>
            <span className="card__subway-text" title={spot.subwayInfo}>
              {spot.subwayInfo}
            </span>
          </div>
        )}

        {/* Event Schedule Line (if event) */}
        {(spot.isEvent || spot.eventDatesDe) && (
          <div className="card__event-line">
            <span className="card__event-icon">📅</span>
            <span
              className="card__event-text"
              title={
                language === 'kr'
                  ? spot.eventDatesKr || '축제 일정 안내'
                  : language === 'en'
                  ? spot.eventDatesEn || 'Schedule available'
                  : spot.eventDatesDe || 'Termine verfügbar'
              }
            >
              {language === 'kr'
                ? spot.eventDatesKr || '축제 일정 안내'
                : language === 'en'
                ? spot.eventDatesEn || 'Schedule available'
                : spot.eventDatesDe || 'Termine verfügbar'}
            </span>
          </div>
        )}

        {/* Dietary or Charging feature tag */}
        {(spot.isVegetarianOnly ||
          spot.dietType === 'vegan' ||
          spot.category === 'halal_food' ||
          spot.dietType === 'halal' ||
          spot.hasFreeCharging ||
          spot.category === 'charging_spot') && (
          <div className="card__features">
            {(spot.dietType === 'vegan' || spot.isVegetarianOnly) && (
              <span className="feature-pill feature-pill--vegan">
                🌿 {language === 'kr' ? '비건/채식' : 'Veggie/Vegan'}
              </span>
            )}
            {(spot.category === 'halal_food' || spot.dietType === 'halal') && (
              <span className="feature-pill feature-pill--halal">
                🕌 {language === 'kr' ? '할랄' : 'Halal'}
              </span>
            )}
            {(spot.hasFreeCharging || spot.category === 'charging_spot') && (
              <span className="feature-pill feature-pill--charging">
                ⚡ {language === 'kr' ? '무료 충전' : 'Kostenlos laden'}
              </span>
            )}
          </div>
        )}

        {/* Card Actions: Primary KakaoMap + Taxi driver card */}
        <div className="card__actions" style={{ flexDirection: 'column', gap: 6 }}>
          {spot.id === 'event-gyeonggi-everland-blood-city' && (
            <a
              href="/everland-map.html"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '9px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'linear-gradient(135deg, #10b981 0%, #0284c7 100%)',
                color: '#ffffff',
                fontSize: '12.5px',
                fontWeight: 800,
                textDecoration: 'none',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.35)',
              }}
            >
              <span>
                {language === 'kr'
                  ? '🗺️ 에버랜드 전체 맵·날씨·맛집 열기'
                  : language === 'en'
                  ? '🗺️ Open Full Everland Map, Weather & Dining'
                  : '🗺️ Everland Park-Map, Wetter & Restaurants öffnen'}
              </span>
              <ExternalLink style={{ width: 14, height: 14 }} />
            </a>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openKakaoMap(spot, false, userLocation);
            }}
            className="btn btn--kakao"
            style={{ width: '100%' }}
            title={
              language === 'kr'
                ? '카카오맵에서 열기'
                : language === 'en'
                ? 'Open in KakaoMap'
                : 'In KakaoMap öffnen'
            }
          >
            <span>
              {language === 'kr'
                ? '카카오맵에서 열기'
                : language === 'en'
                ? 'Open in KakaoMap'
                : 'In KakaoMap öffnen'}
            </span>
            <ExternalLink style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
    </article>
  );
};
